// Phase 4.3: Dashboard API Client with Workspace Header Enforcement
import { api } from "@/lib/api";
import { getWorkspaceHeader, WorkspaceRequiredError } from "./workspace-header";
import {
  DashboardEntitySchema,
  DashboardListRefSchema,
  DashboardTemplateSchema,
  AISuggestResponseSchema,
  AIGenerateResponseSchema,
} from "./schemas";
import type {
  DashboardEntity,
  DashboardTemplate,
  AISuggestResponse,
  AIGenerateResponse,
} from "./types";

/**
 * DASH-LOAD-FAIL-1 / DASH-WIDGET-RESILIENCE-1 — when the backend envelope
 * includes `meta`, the axios interceptor returns `{ __zephixInner, __zephixMeta }`.
 * Local unwrap (not imported from @/lib/api) so incomplete api mocks in tests
 * do not break every dashboard caller.
 */
function unwrapDashboardPayload<T = unknown>(response: unknown): T {
  if (response && typeof response === "object" && "__zephixInner" in response) {
    return (response as { __zephixInner: T }).__zephixInner;
  }
  if (response && typeof response === "object" && "data" in response) {
    return (response as { data: T }).data;
  }
  return response as T;
}

function parseDashboardEntity(response: unknown): DashboardEntity {
  return DashboardEntitySchema.parse(
    unwrapDashboardPayload(response),
  ) as DashboardEntity;
}

// Legacy type for backward compatibility
export type Dashboard = {
  id: string;
  name: string;
  workspaceId: string;
  orgId?: string;
  widgets?: any[];
  filters?: any;
  version?: number;
  etag?: string;
  deletedAt?: string | null;
  visibility?: 'private' | 'workspace' | 'org';
};

/**
 * Get headers with workspace ID and optional additional headers
 */
function getHeaders(additionalHeaders?: Record<string, string>): Record<string, string> {
  const workspaceHeader = getWorkspaceHeader();
  return {
    ...workspaceHeader,
    ...additionalHeaders,
  };
}

/**
 * Fetch a single dashboard by ID
 * Includes x-workspace-id header and validates response with zod
 */
export async function fetchDashboard(id: string): Promise<DashboardEntity> {
  try {
    const response = await api.get(`/api/dashboards/${id}`, {
      headers: getHeaders(),
    });

    return parseDashboardEntity(response);
  } catch (error: unknown) {
    if (error instanceof WorkspaceRequiredError) {
      throw error;
    }
    const err = error as { response?: { headers?: Record<string, string>; data?: unknown } };
    const requestId = err?.response?.headers?.['x-request-id'];
    if (requestId && err?.response?.data) {
      console.error(`Dashboard schema invalid (requestId: ${requestId}):`, err.response.data);
      throw new Error(`Dashboard schema invalid. RequestId: ${requestId}`);
    }
    throw error;
  }
}

/**
 * List all dashboards
 * Includes x-workspace-id header
 */
export async function listDashboards(): Promise<DashboardEntity[]> {
  const response = await api.get(`/api/dashboards`, {
    headers: getHeaders(),
  });

  const payload = unwrapDashboardPayload<unknown>(response);
  if (Array.isArray(payload)) {
    return payload.map((item) => parseDashboardEntity(item));
  }
  if (
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as { data?: unknown }).data)
  ) {
    return ((payload as { data: unknown[] }).data).map((item) =>
      parseDashboardEntity(item),
    );
  }
  return [];
}

/**
 * Sidebar availability only.
 * The list endpoint often omits `widgets`, so full DashboardEntitySchema parsing
 * would throw and incorrectly hide a nonempty Dashboards section.
 * Returns only { id, name }; skips malformed rows (fail closed per row).
 */
export async function listAccessibleDashboardRefs(): Promise<
  Array<{ id: string; name: string }>
> {
  const response = await api.get(`/api/dashboards`, {
    headers: getHeaders(),
  });
  const payload = unwrapDashboardPayload<unknown>(response);
  const rows = Array.isArray(payload)
    ? payload
    : payload &&
        typeof payload === "object" &&
        Array.isArray((payload as { data?: unknown }).data)
      ? (payload as { data: unknown[] }).data
      : null;
  // Non-array / unexpected envelope → empty (fail closed; no uncertain nav row).
  if (!rows) return [];
  const refs: Array<{ id: string; name: string }> = [];
  for (const item of rows) {
    const parsed = DashboardListRefSchema.safeParse(item);
    if (parsed.success) {
      refs.push({ id: parsed.data.id, name: parsed.data.name });
    }
  }
  return refs;
}

/**
 * Create a new dashboard
 * Includes x-workspace-id header
 */
export async function createDashboard(payload: Partial<DashboardEntity>): Promise<DashboardEntity> {
  const response = await api.post(`/api/dashboards`, payload, {
    headers: getHeaders({
      "Idempotency-Key": crypto.randomUUID(),
    }),
  });

  return parseDashboardEntity(response);
}

/**
 * Phase 4.7: dashboard PATCH payload — explicitly excludes `widgets`.
 *
 * Widgets MUST be persisted through the canonical widget endpoints
 * (createWidget / updateWidget / deleteWidget). The backend
 * UpdateDashboardDto does not accept `widgets`, and silently dropping it
 * caused the Phase 4.6 audit's "Add Card silently fails" bug.
 */
export type DashboardPatchPayload = Partial<
  Pick<DashboardEntity, 'name' | 'description' | 'visibility' | 'workspaceId'>
> & {
  /** Optional grid layout config — separate from widgets, persisted via dashboard PATCH. */
  layoutConfig?: Record<string, unknown>;
};

/**
 * Update a dashboard (metadata + layoutConfig only — never widgets).
 * Includes x-workspace-id header.
 */
export async function patchDashboard(
  id: string,
  payload: DashboardPatchPayload,
  etag?: string
): Promise<DashboardEntity> {
  // Phase 4.7 hard guard: refuse to silently drop a `widgets` field that a
  // caller may have sent by mistake. Earlier code paths sent widgets here
  // and the server ignored them — making the UI look like it saved when
  // nothing actually persisted.
  if ('widgets' in (payload as Record<string, unknown>)) {
    throw new Error(
      'patchDashboard: `widgets` is not a valid dashboard PATCH field. ' +
        'Use createWidget / updateWidget / deleteWidget instead.',
    );
  }

  const headers: Record<string, string> = getHeaders();
  if (etag) {
    headers["If-Match"] = etag;
  }

  const response = await api.patch(`/api/dashboards/${id}`, payload, { headers });
  return parseDashboardEntity(response);
}

/* ────────── Phase 4.7: canonical widget endpoints ────────── */

interface WidgetCreatePayload {
  widgetKey: string;
  title: string;
  config: Record<string, unknown>;
  layout: { x: number; y: number; w: number; h: number };
}
interface WidgetUpdatePayload {
  widgetKey?: string;
  title?: string;
  config?: Record<string, unknown>;
  layout?: { x?: number; y?: number; w?: number; h?: number };
}

/** Create a widget on a dashboard via POST /api/dashboards/:id/widgets. */
export async function createWidget(
  dashboardId: string,
  payload: WidgetCreatePayload,
): Promise<unknown> {
  return api.post(`/api/dashboards/${dashboardId}/widgets`, payload, {
    headers: getHeaders(),
  });
}

/** Update an existing widget via PATCH /api/dashboards/:id/widgets/:widgetId. */
export async function updateWidget(
  dashboardId: string,
  widgetId: string,
  payload: WidgetUpdatePayload,
): Promise<unknown> {
  return api.patch(
    `/api/dashboards/${dashboardId}/widgets/${widgetId}`,
    payload,
    { headers: getHeaders() },
  );
}

/** Delete a widget via DELETE /api/dashboards/:id/widgets/:widgetId. */
export async function deleteWidget(
  dashboardId: string,
  widgetId: string,
): Promise<void> {
  await api.delete(`/api/dashboards/${dashboardId}/widgets/${widgetId}`, {
    headers: getHeaders(),
  });
}

/**
 * Delete a dashboard
 * Includes x-workspace-id header
 */
export async function deleteDashboard(id: string): Promise<void> {
  await api.delete(`/api/dashboards/${id}`, {
    headers: getHeaders(),
  });
}

/**
 * Duplicate a dashboard
 * Includes x-workspace-id header
 */
export async function duplicateDashboard(id: string): Promise<DashboardEntity> {
  const response = await api.post(`/api/dashboards/${id}/duplicate`, {}, {
    headers: getHeaders(),
  });
  return parseDashboardEntity(response);
}

/**
 * Restore a dashboard
 * Includes x-workspace-id header
 */
export async function restoreDashboard(id: string): Promise<DashboardEntity> {
  const response = await api.post(`/api/dashboards/${id}/restore`, {}, {
    headers: getHeaders(),
  });
  return parseDashboardEntity(response);
}

/**
 * List dashboard templates
 * Includes x-workspace-id header
 */
export async function listTemplates(): Promise<DashboardTemplate[]> {
  const response = await api.get(`/api/dashboards/templates`, {
    headers: getHeaders(),
  });

  const payload = unwrapDashboardPayload<unknown>(response);
  if (Array.isArray(payload)) {
    return payload.map(
      (item) => DashboardTemplateSchema.parse(item) as DashboardTemplate,
    );
  }
  if (
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as { data?: unknown }).data)
  ) {
    return ((payload as { data: unknown[] }).data).map(
      (item) => DashboardTemplateSchema.parse(item) as DashboardTemplate,
    );
  }
  return [];
}

/**
 * Activate a dashboard template
 * Includes x-workspace-id header
 */
export async function activateTemplate(templateKey: string): Promise<DashboardEntity> {
  const response = await api.post(
    `/api/dashboards/activate-template`,
    { templateKey },
    {
      headers: getHeaders(),
    }
  );

  return parseDashboardEntity(response);
}

/**
 * AI Suggest templates and widgets
 * Includes x-workspace-id header
 * Note: Backend expects persona enum, not prompt for suggest endpoint
 */
export async function aiSuggest(prompt: string, persona?: string): Promise<AISuggestResponse> {
  // Backend requires persona enum. Map prompt keywords to persona or use provided/default
  // Persona enum values: RESOURCE_MANAGER, PMO, EXEC, PROGRAM_MANAGER, PROJECT_MANAGER, DELIVERY_LEAD
  let mappedPersona = persona || "PMO"; // Default to PMO

  // Simple keyword-based mapping from prompt
  const promptLower = prompt.toLowerCase();
  if (promptLower.includes("resource") || promptLower.includes("utilization")) {
    mappedPersona = "RESOURCE_MANAGER";
  } else if (promptLower.includes("exec") || promptLower.includes("executive") || promptLower.includes("portfolio")) {
    mappedPersona = "EXEC";
  } else if (promptLower.includes("program")) {
    mappedPersona = "PROGRAM_MANAGER";
  } else if (promptLower.includes("project manager") || promptLower.includes("pm")) {
    mappedPersona = "PROJECT_MANAGER";
  } else if (promptLower.includes("delivery")) {
    mappedPersona = "DELIVERY_LEAD";
  }

  const response = await api.post(
    `/api/ai/dashboards/suggest`,
    { persona: mappedPersona },
    {
      headers: getHeaders(),
    }
  );

  return AISuggestResponseSchema.parse(
    unwrapDashboardPayload(response),
  ) as AISuggestResponse;
}

/**
 * AI Generate dashboard schema
 * Includes x-workspace-id header
 */
export async function aiGenerate(prompt: string, persona?: string): Promise<AIGenerateResponse> {
  const response = await api.post(
    `/api/ai/dashboards/generate`,
    { prompt, persona },
    {
      headers: getHeaders(),
    }
  );

  return AIGenerateResponseSchema.parse(
    unwrapDashboardPayload(response),
  ) as AIGenerateResponse;
}

// DASH-SHARE-UI-OFF-1 — public share-enable / share-disable / fetchDashboardPublic removed (API 410).

// ── Dashboard Publishing (Batch 4) ──

export async function publishDashboard(
  dashboardId: string,
  audience: string[] = ['MEMBER', 'VIEWER'],
  setAsDefault?: boolean,
): Promise<DashboardEntity> {
  const headers = getWorkspaceHeader();
  const response = await api.post(`/api/dashboards/${dashboardId}/publish`, { audience, setAsDefault }, { headers });
  return parseDashboardEntity(response);
}

export async function unpublishDashboard(dashboardId: string): Promise<void> {
  const headers = getWorkspaceHeader();
  await api.post(`/api/dashboards/${dashboardId}/unpublish`, {}, { headers });
}

export async function updateDashboardAudience(
  dashboardId: string,
  audience: string[],
): Promise<DashboardEntity> {
  const headers = getWorkspaceHeader();
  const response = await api.patch(`/api/dashboards/${dashboardId}/audience`, { audience }, { headers });
  return parseDashboardEntity(response);
}

export async function listPublishedDashboards(workspaceId: string): Promise<DashboardEntity[]> {
  const headers = getWorkspaceHeader();
  const response = await api.get(`/api/dashboards/published/workspace/${workspaceId}`, { headers });
  const arr = unwrapDashboardPayload<unknown>(response);
  return Array.isArray(arr) ? arr.map((item) => parseDashboardEntity(item)) : [];
}

// Phase 3A: Standalone set/unset default (decoupled from publish)

export async function setDashboardDefault(dashboardId: string): Promise<DashboardEntity> {
  const headers = getWorkspaceHeader();
  const response = await api.post(`/api/dashboards/${dashboardId}/set-default`, {}, { headers });
  return parseDashboardEntity(response);
}

export async function unsetDashboardDefault(dashboardId: string): Promise<DashboardEntity> {
  const headers = getWorkspaceHeader();
  const response = await api.post(`/api/dashboards/${dashboardId}/unset-default`, {}, { headers });
  return parseDashboardEntity(response);
}
